import React, { useState, useEffect } from "react";
import { Avatar, Button, Upload, UploadProps, GetProp, message } from "antd";
import { RcFile } from "antd/es/upload";
import { ReactComponent as ExportIcon } from "assets/images/export-icon.svg";
import "./LogoUpload.scss";

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

export interface LogoUploadProps {
  /** URL da imagem atual para preview */
  value?: string;
  /** Callback chamado quando a imagem é alterada */
  onChange?: (
    file: FileType | undefined,
    previewUrl: string | undefined
  ) => void;
  /** Callback opcional quando há erro no upload */
  onError?: (error: string) => void;
  /** Tamanho máximo do arquivo em MB (padrão: 2MB) */
  maxSizeMB?: number;
  /** Tipos de arquivo aceitos (padrão: ['image/jpeg', 'image/png']) */
  accept?: string[];
  /** Tamanho do avatar quando há preview (padrão: 100) */
  avatarSize?: number;
  /** Se está em estado de loading */
  loading?: boolean;
  /** Label do botão de remover (padrão: "Remove") */
  removeLabel?: string;
  /** Texto do placeholder quando não há imagem (padrão: "Drag and drop images") */
  placeholderText?: string;
  /** Texto alternativo do placeholder (padrão: "or") */
  orText?: string;
  /** Texto do botão de seleção (padrão: "Select files") */
  selectButtonText?: string;
  /** Callback para rastrear clique de seleção */
  onSelectClick?: () => void;
}

const getBase64FromFile = (
  img: RcFile,
  callback: (url: string) => void
): void => {
  const reader = new FileReader();
  reader.addEventListener("load", () => {
    const result = reader.result;
    // Ensure result is a string (base64 data URL)
    if (typeof result === "string") {
      callback(result);
    } else {
      console.error("FileReader result is not a string:", result);
    }
  });
  reader.addEventListener("error", (error) => {
    console.error("FileReader error:", error);
  });
  reader.readAsDataURL(img);
};

const LogoUpload: React.FC<LogoUploadProps> = ({
  value,
  onChange,
  onError,
  maxSizeMB = 2,
  accept = ["image/jpeg", "image/png"],
  avatarSize = 100,
  loading = false,
  removeLabel = "Remove",
  placeholderText = "Drag and drop images",
  orText = "or",
  selectButtonText = "Select files",
  onSelectClick,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(value);
  // const [currentFile, setCurrentFile] = useState<FileType | undefined>();

  useEffect(() => {
    setPreviewUrl(value);
  }, [value]);

  const handleRemove = () => {
    setPreviewUrl(undefined);
    // setCurrentFile(undefined);
    onChange?.(undefined, undefined);
  };

  const beforeUpload = (file: FileType): boolean => {
    const isValidType = accept.includes(file.type);
    if (!isValidType) {
      const errorMsg = `You can only upload ${accept
        .map((t) => t.split("/")[1].toUpperCase())
        .join("/")} file!`;
      message.error(errorMsg);
      onError?.(errorMsg);
      return false;
    }

    const isValidSize = file.size / 1024 / 1024 < maxSizeMB;
    if (!isValidSize) {
      const errorMsg = `Image must smaller than ${maxSizeMB}MB!`;
      message.error(errorMsg);
      onError?.(errorMsg);
      return false;
    }

    return false; // Prevent auto upload
  };

  const handleChange: UploadProps["onChange"] = (info) => {
    const file = info.fileList[0];

    if (!file?.originFileObj) {
      // If file is removed or cleared
      if (info.fileList.length === 0) {
        setPreviewUrl(undefined);
        // setCurrentFile(undefined);
        onChange?.(undefined, undefined);
      }
      return;
    }

    const originFile = file.originFileObj;
    if (!originFile) {
      console.warn("File object has no originFileObj");
      return;
    }

    // setCurrentFile(originFile);
    getBase64FromFile(originFile, (url) => {
      // Ensure url is a string before setting
      if (typeof url === "string" && url.startsWith("data:image/")) {
        setPreviewUrl(url);
        // Explicitly call onChange with correct parameter order: (file, url)
        onChange?.(originFile, url);
      } else {
        console.error("Invalid base64 URL generated:", typeof url, url);
      }
    });
  };
  return (
    <div className="logo-upload">
      {previewUrl && (
        <div
          className="logo-upload__remove-container"
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            textDecoration: "underline",
          }}
        >
          <span
            className="logo-upload__remove"
            style={{ cursor: "pointer" }}
            onClick={handleRemove}
          >
            {removeLabel}
          </span>
        </div>
      )}
      <div onClick={onSelectClick}>
        <Upload
          showUploadList={false}
          beforeUpload={beforeUpload}
          onChange={handleChange}
          maxCount={1}
        >
          {previewUrl ? (
            <div className="logo-upload__preview">
              <Avatar src={previewUrl} size={avatarSize} />
            </div>
          ) : (
            <div className="logo-upload__dropzone">
              <ExportIcon />
              <span className="logo-upload__text">{placeholderText}</span>
              <span>{orText}</span>
              <Button type="primary" loading={loading}>
                {selectButtonText}
              </Button>
            </div>
          )}
        </Upload>
      </div>
    </div>
  );
};

export default LogoUpload;
